import { useEffect, useState } from 'react';
import { ROLES, ROLE_LABEL, type LabGeofence, type Role } from '@/domain';
import { useGeofence, useSaveGeofence } from '@/application/usecases/useAdmin';
import { useLocationProvider } from '@/application/container';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { LocationIcon } from '@/ui/icons';
import styles from './UsersScreen.module.css';

/**
 * Station sign in by location. Section 4.2, and the client's request.
 *
 * BE CLEAR ABOUT WHAT THIS IS. The check runs in the browser, and a browser can
 * be told to report any coordinates at all. It stops somebody signing in from
 * the car park by accident; it does not stop somebody determined to. Real
 * enforcement belongs on the server, which sees the request IP, and that is
 * written up for the backend developer. The screen says so too, because a
 * manager who believes this is a lock would make worse decisions than one who
 * knows it is a fence.
 *
 * The fields and Use my location change a draft; Save is asked about before it
 * is sent. The request of 5 October 2026 (third), section 2.
 */
export function GeofenceCard() {
  const { data } = useGeofence();
  const save = useSaveGeofence();
  const ask = useConfirmAction();
  const location = useLocationProvider();
  const [draft, setDraft] = useState<LabGeofence | null>(null);

  useEffect(() => {
    if (data !== undefined) setDraft(data);
  }, [data]);

  if (draft === null) return null;

  const update = (change: Partial<LabGeofence>) => setDraft({ ...draft, ...change });

  function toggleRole(role: Role, on: boolean) {
    if (draft === null) return;
    update({
      appliedRoles: on
        ? [...draft.appliedRoles, role]
        : draft.appliedRoles.filter((candidate) => candidate !== role),
    });
  }

  async function takeCurrentLocation() {
    const here = await location.current();
    if (here === null) {
      toast(strings.geofence.locationRefused);
      return;
    }
    update({ latitude: here.latitude, longitude: here.longitude });
    toast(strings.geofence.locationTaken);
  }

  return (
    <>
      <div className={`${styles.section} ${styles.spaced}`}>
        <span className={styles.lbl}>{strings.geofence.label}</span>
        <h3>{strings.geofence.title}</h3>
        <span className={styles.hint}>{strings.geofence.hint}</span>
      </div>

      <div className={styles.fenceCard}>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => update({ enabled: event.target.checked })}
          />
          {strings.geofence.enable}
        </label>

        <div className={styles.fenceGrid}>
          <Field label={strings.geofence.latitude}>
            <input
              className={styles.fenceInput}
              type="number"
              step="0.000001"
              value={draft.latitude}
              onChange={(event) => update({ latitude: Number(event.target.value) })}
            />
          </Field>
          <Field label={strings.geofence.longitude}>
            <input
              className={styles.fenceInput}
              type="number"
              step="0.000001"
              value={draft.longitude}
              onChange={(event) => update({ longitude: Number(event.target.value) })}
            />
          </Field>
          <Field label={strings.geofence.radius}>
            <input
              className={styles.fenceInput}
              type="number"
              min={20}
              value={draft.radiusMetres}
              onChange={(event) => update({ radiusMetres: Number(event.target.value) })}
            />
          </Field>
        </div>

        <div className={styles.fenceRoles}>
          <span className={styles.l}>{strings.geofence.appliesTo}</span>
          {ROLES.map((role) => (
            <label key={role} className={styles.check}>
              <input
                type="checkbox"
                checked={draft.appliedRoles.includes(role)}
                onChange={(event) => toggleRole(role, event.target.checked)}
              />
              {ROLE_LABEL[role]}
            </label>
          ))}
        </div>

        <p className={styles.checkHint}>{strings.geofence.advisory}</p>

        <div className={styles.fenceActions}>
          <Button icon={<LocationIcon />} onClick={() => void takeCurrentLocation()}>
            {strings.geofence.useHere}
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending}
            onClick={() =>
              ask(
                strings.confirm.geofence(draft.enabled),
                () => save.mutateAsync(draft).then(() => toast(strings.geofence.saved)),
                { title: strings.geofence.title, confirmLabel: strings.geofence.save },
              )
            }
          >
            {save.isPending ? strings.geofence.saving : strings.geofence.save}
          </Button>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className={styles.l}>{label}</span>
      {children}
    </div>
  );
}
